"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { formatCurrency } from "@/utils/currency";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";

interface Transaction {
  id: string;
  type: "credit" | "debit";
  amount: number;
  description: string;
  status?: "completed" | "pending" | "failed";
  date: string;
  reference?: string;
}

const getStatusVariant = (status: Transaction['status']) => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "destructive";
    default:
      return "secondary";
  }
};

const getTypeIcon = (type: Transaction['type']) => {
  return type === "credit" 
    ? <ArrowDownLeft className="h-4 w-4 text-green-600" />
    : <ArrowUpRight className="h-4 w-4 text-red-600" />;
};

export const columns: ColumnDef<Transaction>[] = [
  {
    accessorKey: "type",
    size: 80,
    minSize: 60,
    maxSize: 100,
    header: () => <span className="text-base font-bold">Type</span>,
    cell: ({ row }) => {
      const type = row.getValue("type") as Transaction['type'];
      return (
        <div className="flex items-center gap-2">
          {getTypeIcon(type)}
          <span className="text-xs font-medium capitalize">{type}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "description",
    size: 200,
    minSize: 150,
    maxSize: 300,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Description</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const description = row.getValue("description") as string;
      const reference = row.original.reference;
      
      return (
        <div>
          <h3 className="text-xs font-semibold">{description}</h3>
          {reference && (
            <span className="text-xs text-muted-foreground font-mono">{reference}</span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "amount",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Amount</span>
          <div className={`transition-opacity duration-200 ${column.getIsSorted() ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const amount = row.getValue("amount") as number;
      const type = row.original.type;
      
      return (
        <div className={`font-semibold text-xs ${
          type === "credit" ? "text-green-600" : "text-red-600"
        }`}>
          {type === "credit" ? "+" : "-"}{formatCurrency(amount)}
        </div>
      );
    },
  },
  {
    accessorKey: "date",
    size: 140,
    minSize: 120,
    maxSize: 180,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Date</span>
          <div className={`transition-opacity duration-200 ${column.getIsSorted() ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const dateString = row.getValue("date") as string;
      const date = new Date(dateString);
      if (!dateString || Number.isNaN(date.getTime())) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      const formattedDate = format(date, "MMM dd, yyyy");
      const formattedTime = format(date, "HH:mm");
      
      return (
        <div>
          <h3 className="text-xs font-semibold">{formattedDate}</h3>
          <span className="text-xs text-muted-foreground">{formattedTime}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = row.getValue("status") as Transaction['status'];
      const label = status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : "Unknown";

      return (
        <Badge variant={getStatusVariant(status) as any}>
          {label}
        </Badge>
      );
    },
  },
];
